/**
 * History — ADR-007 §2.2/§3.3: retained frozen Documents, undo/redo as
 * CURSOR MOVES over them. Undo restores the prior state because it IS the
 * prior state (the identical frozen object) — no inverse per verb exists to
 * be wrong or forgotten, redo retrieves and never recomputes, and dirty is an
 * identity comparison (undoing back to the saved state un-dirties by itself).
 * Trimming advances `baseDocument` to the state before the first retained
 * entry, so a fold of the retained commands over the base always reproduces
 * the current state (Codex P2, #51). Memory is priced as measured, not as
 * assumed shared (§3.3): the gate rebuilds the tree, so every entry is a full
 * copy — and now that authored keys live in the Document (§13.4), retention
 * is bounded by MEASURED serialized weight as well as by declared count
 * (Codex P2, #61 r4).
 *
 * A pure value + pure functions; the app store HOLDS it (§4) — session-
 * scoped: the History is the sitting, the Document is the work (§3.3).
 * Depends on: session/document, session/commands, ir errors. Depended on by:
 * the app store, session tests, replay flows.
 */
import { SchemaValidationError } from '../ir/errors.js';
import { applyCommand, parseCommand, type Command } from './commands.js';
import type { AnimatorDocument } from './document.js';

/** §12.1 (decided): the default is a DECLARATION a run reproduces under… */
export const HISTORY_CAPACITY_DEFAULT = 100;
/** …and the ceiling is what a user may explicitly choose in settings. */
export const HISTORY_CAPACITY_MAX = 250;
/**
 * §3.3's re-measure debt, paid (Codex P2, #61 r4): now that authored clips
 * live in the Document, a retained entry is no longer guaranteed KB-scale —
 * the gate is a full reparse, so a loaded clip's every key is CLONED into
 * every entry. Retention is therefore bounded by MEASURED serialized weight
 * as well as by count: entries trim oldest-first once their summed weight
 * exceeds this budget. 64 MiB serialized (heap ≈ 2–4× that) keeps a 10k-key
 * document undoable ~51 entries deep — measured 2026-07-25: 125 bytes/key
 * serialized with realistic float64 rotations, 72 with identity keys — while
 * a pose-only sitting never notices the bound exists.
 */
export const HISTORY_BYTE_BUDGET_DEFAULT = 64 * 1024 * 1024;

export interface HistoryEntry {
  readonly command: Command;
  /** The frozen Document this command produced — retained truth, not a delta. */
  readonly document: AnimatorDocument;
  /** The document's serialized weight, measured ONCE at dispatch (JSON code units ≈ bytes). */
  readonly bytes: number;
}

export interface History {
  /**
   * The lineage's replay base: the state before entries[0]. Advances on trim
   * (Codex P2, #51) — and it is undo's floor, which the surface reports as a
   * disabled-with-reason, never a dead button (§3.3).
   */
  readonly baseDocument: AnimatorDocument;
  readonly entries: readonly HistoryEntry[];
  /** How many entries are currently applied: 0..entries.length. */
  readonly cursor: number;
  /** What the last save pointed at — dirty is `current !== savedDocument` (§3.3). */
  readonly savedDocument: AnimatorDocument;
  readonly capacity: number;
  /** The measured-weight bound on retained entries (serialized bytes, summed). */
  readonly byteBudget: number;
  /**
   * Why baseDocument last advanced — null until a trim happens. The undo
   * floor's No-Trap reason depends on it: "raise the cap in settings" is only
   * an honest remedy for a COUNT trim; a weight trim needs different words.
   */
  readonly trimCause: 'count' | 'bytes' | null;
}

/** Begin a lineage (§4: loading a Document starts a fresh History). */
export function createHistory(
  document: AnimatorDocument,
  options: { capacity?: number; byteBudget?: number } = {},
): History {
  const capacity = options.capacity ?? HISTORY_CAPACITY_DEFAULT;
  if (!Number.isInteger(capacity) || capacity < 1 || capacity > HISTORY_CAPACITY_MAX) {
    throw new SchemaValidationError('History', [
      `capacity ${capacity} outside the declared band 1–${HISTORY_CAPACITY_MAX} (ADR-007 §12.1)`,
    ]);
  }
  const byteBudget = options.byteBudget ?? HISTORY_BYTE_BUDGET_DEFAULT;
  if (!Number.isInteger(byteBudget) || byteBudget < 1) {
    throw new SchemaValidationError('History', [
      `byteBudget ${byteBudget} is not a positive integer — the weight bound is a measured declaration (§3.3)`,
    ]);
  }
  return {
    baseDocument: document,
    entries: [],
    cursor: 0,
    savedDocument: document,
    capacity,
    byteBudget,
    trimCause: null,
  };
}

/** The Document the sitting is looking at right now. */
export function current(history: History): AnimatorDocument {
  return history.cursor === 0
    ? history.baseDocument
    : (history.entries[history.cursor - 1] as HistoryEntry).document;
}

/**
 * Apply a command to the current Document and record the step: truncates the
 * redo tail (§3.3 — the one lifecycle rule v2 got right), appends, and trims
 * past capacity with the checkpoint advancing. A refused command changes
 * nothing — applyCommand throws before any History math runs.
 */
export function dispatch(history: History, raw: unknown): History {
  // Gate once, store the GATED form (zod strips unknown keys — the retained
  // log holds exactly the envelope, never a caller's extras).
  const command = parseCommand(raw);
  const document = applyCommand(current(history), command);
  // Measured ONCE, here — the gate already walked the whole tree, so the
  // stringify is the same complexity class as the dispatch it prices.
  const bytes = JSON.stringify(document).length;
  const kept = history.entries.slice(0, history.cursor);
  let baseDocument = history.baseDocument;
  let entries: HistoryEntry[] = [...kept, { command, document, bytes }];
  let cursor = history.cursor + 1;
  let trimCause = history.trimCause;
  if (entries.length > history.capacity) {
    const overflow = entries.length - history.capacity;
    // The state before the first RETAINED entry — without this, replay over
    // the retained commands reproduces the wrong state (Codex P2, #51).
    baseDocument = (entries[overflow - 1] as HistoryEntry).document;
    entries = entries.slice(overflow);
    cursor -= overflow;
    trimCause = 'count';
  }
  // The weight bound (§3.3 re-measured; Codex P2, #61 r4): a document heavy
  // with authored keys must not multiply itself capacity times. Oldest trims
  // first, the checkpoint advances the same way — and the floor is ONE entry,
  // so the last edit stays undoable even when a single document outweighs the
  // whole budget (the budget bounds the tail, the floor keeps No-Trap).
  let retained = entries.reduce((sum, e) => sum + e.bytes, 0);
  while (retained > history.byteBudget && entries.length > 1) {
    const first = entries[0] as HistoryEntry;
    baseDocument = first.document;
    retained -= first.bytes;
    entries = entries.slice(1);
    cursor -= 1;
    trimCause = 'bytes';
  }
  return { ...history, baseDocument, entries, cursor, trimCause };
}

export function canUndo(history: History): boolean {
  return history.cursor > 0;
}

export function canRedo(history: History): boolean {
  return history.cursor < history.entries.length;
}

/** A cursor move to retained truth — never an inverse, never a recompute (§2.2). */
export function undo(history: History): History {
  return canUndo(history) ? { ...history, cursor: history.cursor - 1 } : history;
}

/** Forward to a Document already produced and retained — redo cannot diverge (§2.3). */
export function redo(history: History): History {
  return canRedo(history) ? { ...history, cursor: history.cursor + 1 } : history;
}

/** Identity, not equality: undoing back to the saved object un-dirties by itself (§3.3). */
export function isDirty(history: History): boolean {
  return current(history) !== history.savedDocument;
}

export function markSaved(history: History): History {
  return { ...history, savedDocument: current(history) };
}

/**
 * The commands whose fold over `baseDocument` reproduces `current` — the
 * replay surface for provenance/agent flows (§3: never undo's mechanism).
 * Equivalence ceiling: deep-equal within a pinned runtime (ADR-011 §2.4).
 */
export function appliedCommands(history: History): readonly Command[] {
  return history.entries.slice(0, history.cursor).map((e) => e.command);
}
